import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PhotoPickSheet from '../components/catalog/PhotoPickSheet';
import { apiClient } from '../api/client';
import { seedPresetGroup } from '../core/queries/presetGroups';
import useBootstrap from '../hooks/useBootstrap';
import { useCatalog } from '../hooks/useCatalog';
import { localizedField } from '../locales/content';
import { storage } from '../platform';
import { useStore } from '../store';
import { getPhotoCount } from '../utils/photoReferences';
import { hapticImpact, hapticSelection } from '../utils/haptics';
import useConsentGuard from '../hooks/useConsentGuard';
import {
  getSearchRank,
} from '../utils/searchNormalization';

const RECENTS_STORAGE_KEY = 'catalogSearchRecents';
const MAX_RECENTS = 8;
const MAX_SUGGESTIONS = 20;
const DEBOUNCE_MS = 300;

const BADGE_CLASS = {
  group: 'bg-coin-yellow text-black',
  preset: 'bg-white/85 text-black',
  video: 'bg-[#4FA3FF] text-white',
};

const isVideoSource = (url) =>
  typeof url === 'string' && /\.(mp4|webm|mov)(\?.*)?$/i.test(url);

const isLegacyUnversionedPresetImage = (url) =>
  typeof url === 'string' && /^\/img_presets\/[^/?]+\.(webp|jpe?g|png)$/i.test(url);

const firstValue = (...values) =>
  values
    .flat()
    .find((value) =>
      typeof value === 'string' &&
      value.trim() &&
      !isLegacyUnversionedPresetImage(value)
    );

function resolvePresetImage(preset) {
  return firstValue(
    preset?.homeThumbUrl,
    preset?.cardPreviewThumb,
    preset?.cardPreviewImage,
    preset?.coverUrl,
    preset?.thumbnailUrl,
    preset?.image,
    preset?.exampleImages,
  ) || null;
}

function resolveGroupImage(group) {
  return firstValue(
    group?.coverThumb,
    group?.cover,
    group?.homeThumbUrl,
    group?.cardPreviewThumb,
    group?.cardPreviewImage,
    group?.previewSlideshowUrls,
    group?.presets?.[0]?.homeThumbUrl,
    group?.presets?.[0]?.image,
  ) || null;
}

function resolveGroupSuggestionImage(group) {
  return firstValue(
    group?.coverThumb,
    group?.homeThumbUrl,
    group?.cardPreviewThumb,
    group?.presets?.[0]?.homeThumbUrl,
  ) || null;
}

function resolveVideoImage(preset) {
  return firstValue(
    preset?.previewClipUrl,
    preset?.coverUrl,
    preset?.thumbnailUrl,
    preset?.homeThumbUrl,
    preset?.image,
  ) || null;
}

function displayName(item) {
  return localizedField(item, 'name') || item?.name || item?.id || '';
}

function isSearchableGroup(group) {
  return Boolean(
    group?.id &&
    String(group.id).toLowerCase() !== 'general' &&
    displayName(group) &&
    resolveGroupImage(group) &&
    Array.isArray(group.presets) &&
    group.presets.length > 0
  );
}

function isSearchablePreset(preset) {
  return Boolean(
    preset?.id &&
    displayName(preset) &&
    resolvePresetImage(preset)
  );
}

function isSearchableVideo(preset) {
  return Boolean(
    preset?.id &&
    displayName(preset) &&
    resolveVideoImage(preset)
  );
}

function readStoredRecents() {
  try {
    const raw = storage.get(RECENTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((item) => typeof item === 'string' && item.trim()).slice(0, MAX_RECENTS)
      : [];
  } catch {
    return [];
  }
}

function saveStoredRecents(items) {
  try {
    storage.set(RECENTS_STORAGE_KEY, JSON.stringify(items.slice(0, MAX_RECENTS)));
  } catch {
    /* storage failures are non-fatal */
  }
}

function normalizeQuery(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function getSearchCardAspectRatio(type) {
  return type === 'group' ? '1 / 1' : '3 / 4';
}

function collectBootstrapPhotoGroups(bootstrap) {
  const groups = [];
  const seen = new Set();

  for (const section of bootstrap?.sections || []) {
    const candidates = [
      section?.refData,
      ...(Array.isArray(section?.items) ? section.items : []),
    ];

    for (const group of candidates) {
      if (!group?.id || seen.has(String(group.id))) continue;
      seen.add(String(group.id));
      groups.push(group);
    }
  }

  return groups;
}

function dedupeSearchIndex(items) {
  const byId = new Set();
  const unique = [];

  for (const item of items) {
    if (!item?.id) continue;

    const idKey = `${item.type}:${item.payload?.id || item.id}`;
    if (byId.has(idKey)) continue;
    byId.add(idKey);

    unique.push(item);
  }

  return unique;
}

function SearchResultMedia({ item }) {
  const [failed, setFailed] = useState(false);
  const src = item?.image;

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-surface-2"
      />
    );
  }

  if (isVideoSource(src)) {
    return (
      <video
        src={src}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <img
      src={src}
      alt={item.name || ''}
      className="absolute inset-0 h-full w-full object-cover"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

export default function SearchCatalogPage() {
  const { t, i18n } = useTranslation();
  const activeLocale = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(normalizeQuery(searchParams.get('q') || ''));
  const lastSyncedQueryRef = useRef(initialQueryRef.current);
  const queryHistoryEntryActiveRef = useRef(initialQueryRef.current.length > 0);
  const skipNextUrlSyncRef = useRef(false);
  const { data: bootstrap, skeletonData: bootstrapSkeletonData } = useBootstrap();
  const { items: photoGroups } = useCatalog('photo', 'all-photo', {
    refetchOnMount: 'always',
  });
  const { items: videoGroups } = useCatalog('video', 'all-video', {
    refetchOnMount: 'always',
  });
  const basePresetsQuery = useQuery({
    queryKey: ['catalog-search-base-presets'],
    queryFn: async ({ signal }) => {
      const response = await apiClient.generate.getBasePresets(signal);
      return Array.isArray(response?.data?.presets) ? response.data.presets : [];
    },
    refetchOnMount: 'always',
  });
  const generateImage = useStore((state) => state.generateImage);
  const { requireConsent } = useConsentGuard();
  const createInFlightRef = useRef(false);
  const [query, setQuery] = useState(() => initialQueryRef.current);
  const [debouncedQuery, setDebouncedQuery] = useState(() => initialQueryRef.current);
  const [recents, setRecents] = useState(() => readStoredRecents());
  const [activePreset, setActivePreset] = useState(null);
  const [references, setReferences] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sheetError, setSheetError] = useState(null);
  const [stableSuggestions, setStableSuggestions] = useState([]);

  useEffect(() => {
    saveStoredRecents(recents);
  }, [recents]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(normalizeQuery(query));
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    const urlQuery = normalizeQuery(searchParams.get('q') || '');
    if (urlQuery === lastSyncedQueryRef.current) return;

    lastSyncedQueryRef.current = urlQuery;
    queryHistoryEntryActiveRef.current = urlQuery.length > 0;
    skipNextUrlSyncRef.current = true;
    setQuery(urlQuery);
    setDebouncedQuery(urlQuery);
  }, [searchParams]);

  useEffect(() => {
    const nextQuery = normalizeQuery(debouncedQuery);
    const currentQuery = normalizeQuery(searchParams.get('q') || '');
    if (nextQuery === currentQuery) return;
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (nextQuery) nextParams.set('q', nextQuery);
    else nextParams.delete('q');

    const shouldPush =
      currentQuery.length === 0 &&
      nextQuery.length > 0 &&
      !queryHistoryEntryActiveRef.current;

    lastSyncedQueryRef.current = nextQuery;
    if (nextQuery) queryHistoryEntryActiveRef.current = true;
    setSearchParams(nextParams, { replace: !shouldPush });
  }, [debouncedQuery, searchParams, setSearchParams]);

  const suggestionCandidates = useMemo(() => {
    const cachedBootstrapGroups = collectBootstrapPhotoGroups(bootstrap || bootstrapSkeletonData);
    const source = photoGroups?.length ? photoGroups : cachedBootstrapGroups;

    return (source || [])
      .filter(isSearchableGroup)
      .map((group) => ({
        id: String(group.id),
        name: displayName(group),
        image: resolveGroupSuggestionImage(group),
      }))
      .filter((item) => item.name)
      .slice(0, MAX_SUGGESTIONS);
  }, [activeLocale, bootstrap, bootstrapSkeletonData, photoGroups]);

  useEffect(() => {
    if (stableSuggestions.length > 0 || suggestionCandidates.length === 0) return;
    setStableSuggestions(suggestionCandidates);
  }, [stableSuggestions.length, suggestionCandidates]);

  const suggestions = stableSuggestions.length > 0 ? stableSuggestions : suggestionCandidates;

  const searchIndex = useMemo(() => {
    const groupPresetIds = new Set();

    const groups = (photoGroups || [])
      .filter(isSearchableGroup)
      .map((group) => {
        for (const preset of group.presets || []) {
          if (!preset?.id) continue;
          groupPresetIds.add(String(preset.id));
        }

        return {
          id: `group:${group.id}`,
          type: 'group',
          name: displayName(group),
          searchIndex: group.searchIndex,
          image: resolveGroupImage(group),
          payload: group,
        };
      });

    const singlePresets = (basePresetsQuery.data || [])
      .filter((preset) => isSearchablePreset(preset) && !groupPresetIds.has(String(preset.id)))
      .map((preset) => ({
        id: `preset:${preset.id}`,
        type: 'preset',
        name: displayName(preset),
        searchIndex: preset.searchIndex,
        image: resolvePresetImage(preset),
        payload: preset,
      }));

    const videoPresets = (videoGroups || [])
      .flatMap((group) => group?.presets || [])
      .filter(isSearchableVideo)
      .map((preset) => ({
        id: `video:${preset.id}`,
        type: 'video',
        name: displayName(preset),
        searchIndex: preset.searchIndex,
        image: resolveVideoImage(preset),
        payload: preset,
      }));

    return dedupeSearchIndex([...groups, ...singlePresets, ...videoPresets]);
  }, [activeLocale, basePresetsQuery.data, photoGroups, videoGroups]);

  const results = useMemo(() => {
    if (!debouncedQuery) return [];
    return searchIndex
      .map((item, index) => ({ item, index, rank: getSearchRank(item, debouncedQuery) }))
      .filter((entry) => entry.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((entry) => entry.item)
      .slice(0, 120);
  }, [debouncedQuery, searchIndex]);

  const requiredRefCount = activePreset ? getPhotoCount(activePreset) : 1;

  const rememberQuery = useCallback((value) => {
    const next = normalizeQuery(value);
    if (!next) return;
    setRecents((current) => [
      next,
      ...current.filter((item) => item.toLowerCase() !== next.toLowerCase()),
    ].slice(0, MAX_RECENTS));
  }, []);

  const applyQuery = useCallback((value) => {
    const next = normalizeQuery(value);
    if (!next) return;
    hapticSelection();
    setQuery(next);
    rememberQuery(next);
  }, [rememberQuery]);

  const clearQuery = useCallback(() => {
    hapticImpact('light');
    setQuery('');
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    rememberQuery(query);
  };

  const closeSheet = useCallback(() => {
    if (generating) return;
    setActivePreset(null);
    setReferences([]);
    setSheetError(null);
  }, [generating]);

  const openSheet = useCallback((preset) => {
    setActivePreset(preset);
    setReferences([]);
    setSheetError(null);
  }, []);

  const handleResultTap = useCallback((item) => {
    if (!item) return;
    hapticSelection();
    rememberQuery(query || item.name);

    if (item.type === 'group') {
      seedPresetGroup(item.payload);
      navigate(`/group/${item.payload.id}`, { state: { group: item.payload } });
      return;
    }

    if (item.type === 'video') {
      navigate(`/video-generate/${item.payload.id}`);
      return;
    }

    openSheet(item.payload);
  }, [navigate, openSheet, query, rememberQuery]);

  const performCreate = useCallback(async ({ prompt = '', batchCount = 1 } = {}) => {
    if (createInFlightRef.current) return;
    if (!activePreset) return;
    const readyRefs = references.filter(Boolean);
    if (readyRefs.length < requiredRefCount) return;

    createInFlightRef.current = true;
    setGenerating(true);
    setSheetError(null);
    navigate('/gallery');
    try {
      await generateImage(activePreset.id, prompt, '9:16', '1.5K', batchCount, readyRefs);
    } catch {
      setSheetError(t('prompt.genError', 'Generation error'));
    } finally {
      createInFlightRef.current = false;
      setGenerating(false);
    }
  }, [activePreset, generateImage, navigate, references, requiredRefCount, t]);

  const handleCreate = useCallback(
    (options = {}) => requireConsent(() => performCreate(options), 'generate'),
    [performCreate, requireConsent],
  );

  const hasQuery = normalizeQuery(query).length > 0;
  const hasDebouncedQuery = debouncedQuery.length > 0;

  return (
    <div className="min-h-screen bg-bg-app text-text-primary">
      <div
        className="pb-12"
        style={{
          paddingTop:
            'calc(var(--safe-top, 0px) + 88px - var(--page-content-top-shift, 0px))',
        }}
      >
        <div aria-hidden="true" className="h-[10px]" />
        <div
          className="sticky z-30 bg-bg-app px-4 pb-3 relative"
          style={{
            top:
              'calc(var(--safe-top, 0px) + 88px - var(--page-content-top-shift, 0px))',
          }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-full h-40 bg-bg-app pointer-events-none"
          />
          <form onSubmit={handleSubmit}>
            <label className="relative block">
              <span className="sr-only">
                {t('search.placeholder', 'Поиск пресетов и групп')}
              </span>
              <Search
                aria-hidden="true"
                size={20}
                className="absolute left-4 top-1/2 z-[1] -translate-y-1/2 text-text-secondary"
                strokeWidth={2}
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('search.placeholder', 'Поиск пресетов и групп')}
                className="h-12 w-full rounded-home-16 border border-border-default bg-surface-2 py-0 pl-12 pr-12 font-sans text-base font-semibold text-text-primary outline-none placeholder:text-text-secondary focus:border-white/30"
                autoComplete="off"
                inputMode="search"
                enterKeyHint="search"
                data-testid="catalog-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearQuery}
                  className="absolute right-3 top-1/2 z-[1] flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-glass-16 text-text-primary"
                  aria-label={t('common.clear', 'Очистить')}
                  data-testid="catalog-search-clear"
                >
                  <X aria-hidden="true" size={16} strokeWidth={2.25} />
                </button>
              )}
            </label>
          </form>
        </div>

        {!hasQuery ? (
          <div className="mt-6 flex flex-col gap-7">
            {recents.length > 0 && (
              <section className="flex flex-col gap-3 px-4" data-testid="catalog-search-recents">
                <h2 className="font-sans text-h2 text-text-primary" style={{ opacity: 0.9 }}>
                  {t('search.recents', 'Недавние')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {recents.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => applyQuery(item)}
                      className="h-[34px] rounded-full border border-border-default px-4 font-sans text-chip-label font-semibold text-text-primary"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="flex flex-col gap-2 px-4" data-testid="catalog-search-suggestions">
              <h2 className="mb-1 font-sans text-h2 text-text-primary" style={{ opacity: 0.9 }}>
                {t('search.suggestions', 'Популярное')}
              </h2>
              {suggestions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => applyQuery(item.name)}
                  className="flex min-h-[48px] items-center gap-3 rounded-home-8 px-1 text-left"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-2 text-text-secondary"
                    data-testid="catalog-search-suggestion-thumb"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt=""
                        width={36}
                        height={36}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                      />
                    ) : (
                      <Search aria-hidden="true" size={18} strokeWidth={2} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-sans text-base font-semibold text-text-primary">
                    {item.name}
                  </span>
                </button>
              ))}
            </section>
          </div>
        ) : (
          <section className="mt-5 px-4" data-testid="catalog-search-results">
            {hasDebouncedQuery && results.length === 0 ? (
              <div className="py-20 text-center font-sans text-sm font-semibold text-text-secondary">
                {t('search.noResults', 'Ничего не найдено')}
              </div>
            ) : (
              <div style={{ columnCount: 2, columnGap: '12px' }}>
                {results.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleResultTap(item)}
                    className="relative mb-3 w-full overflow-hidden rounded-home-16 bg-surface-2 text-left"
                    style={{
                      aspectRatio: getSearchCardAspectRatio(item.type),
                      breakInside: 'avoid',
                      WebkitColumnBreakInside: 'avoid',
                    }}
                  >
                    <SearchResultMedia item={item} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <span
                      className={[
                        'absolute left-2 top-2 z-[2] rounded-full px-2.5 py-1',
                        'font-sans text-[11px] font-bold leading-none',
                        BADGE_CLASS[item.type],
                      ].join(' ')}
                    >
                      {item.type === 'group'
                        ? t('search.badgeGroup', 'Packs')
                        : item.type === 'video'
                          ? t('search.badgeVideo', 'Video')
                          : t('search.badgePreset', 'Пресет')}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 z-[2] p-3">
                      <h3 className="line-clamp-2 font-sans text-sm font-bold leading-4 text-white">
                        {item.name}
                      </h3>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      <PhotoPickSheet
        preset={activePreset}
        references={references}
        requiredCount={requiredRefCount}
        loading={generating}
        error={sheetError}
        onClose={closeSheet}
        onReferencesChange={setReferences}
        onCreate={handleCreate}
      />
    </div>
  );
}
